import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function LoadingScreen({ onComplete }) {
  const [phase, setPhase] = useState("reveal"); // reveal -> text -> exit

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("text"), 1200);
    const t2 = setTimeout(() => setPhase("exit"), 3000);
    const t3 = setTimeout(() => onComplete(), 3500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onComplete]);

  return (
    <AnimatePresence>
      {phase !== "exit" ? (
        <motion.div
          key="loader"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[9999] bg-[#050505] flex flex-col items-center justify-center overflow-hidden"
          data-testid="loading-screen"
        >
          {/* Ambient glow behind logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 0.18, scale: 1.3 }}
            transition={{ duration: 1.8, ease: "easeOut" }}
            className="absolute w-[300px] h-[300px] rounded-full"
            style={{ background: "radial-gradient(circle, #E5A800 0%, transparent 70%)" }}
          />

          {/* Horizontal reveal line */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="absolute w-24 h-[1px] bg-gradient-to-r from-transparent via-[#E5A800]/60 to-transparent"
            style={{ top: "calc(50% - 60px)", originX: 0.5 }}
          />

          {/* Logo icon */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6, filter: "blur(14px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="relative z-10 mb-6"
          >
            <img
              src="/oc-logo.png"
              alt="OBRA Creatives"
              className="w-20 h-auto"
              style={{ filter: "drop-shadow(0 0 30px rgba(229, 168, 0, 0.3))" }}
            />
          </motion.div>

          {/* Brand text */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={phase === "text" || phase === "exit" ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="relative z-10 text-center"
          >
            <h1 className="text-[#EDEDED] text-lg tracking-[0.35em] font-light uppercase">
              OBRA Creatives
            </h1>
            <motion.div
              initial={{ scaleX: 0 }}
              animate={phase === "text" || phase === "exit" ? { scaleX: 1 } : {}}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
              className="h-[1px] mt-2 bg-gradient-to-r from-transparent via-[#D4C5B0]/50 to-transparent origin-center"
            />
          </motion.div>

          {/* Subtle corner accents */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.2 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="absolute top-8 left-8 w-8 h-8 border-t border-l border-[#D4C5B0]/30"
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.2 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="absolute bottom-8 right-8 w-8 h-8 border-b border-r border-[#D4C5B0]/30"
          />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
